import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  first,
  map,
  merge,
  Observable,
  of,
  pairwise,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import { SlActions } from '../ActionsService/sl-actions';
import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import { SlNavigationService } from '../sl-navigation.service';
import {
  getFlashMessages,
  getFooterButtons,
  getLessonQuitPromptPopup,
  getLoseProgressPromptPopup,
  NonRemovableLessonSessionRoutes,
  SlLEContext,
  SUPPORTED_SCREENS,
} from '../../global-constants';
import {
  ActionType,
  CurrentPageType,
  IFooterButton,
  ILpComments,
  LessonQuitPopupType,
  MaterialCTAClickType,
  MaterialQuitAction,
  QuizFeedbackType,
  RequestType,
  TrackActionName,
} from '../../global-interface';
import { getLEHeaders } from '../../utils/utilsFunctions';
import { SlStorageKeys } from '../../../../shared/public/globalInterfaces/globalInterfaces';
import { IFlashMessageTypes } from '../../../../shared/public/SlFlashMessage/SlFlashMessage';
import { shuffleArray } from '../../../../shared/public/utilsPublic/utilsPublic';
import { IError, ErrorCodes } from '../../../../api/private/global.interface';
import {
  IMaterial, MaterialType, LessonSessionQuitType, ILessonSubtree, LocationType, AnswerType, IAnswerOption, AnswerOptionStatus, IMaterialSolutionSubmission, IMaterialStatus, CompletionStatus, VisibilityStatus, MaterialRequirementType, IMaterialSolutionDTO, IScreenConfig, ScreenSource, ScreenName, ILearningPathSubtree, UIComponentType,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlErrorHandlerService } from '../../../../api/public/error-handling';
import { Service, Container, StreakService, HeartService, BitService, StorageService, DataService, SessionRoutesService, SessionRouteEnums, ShopItemType, history, SubscriptionPlanService, i18n } from '../../../../symphony';
import { PlanConfigKey, Touchpoint } from '../../../../api/public/subscription-api';
import { DynamicPaywallLoader } from '../../../../_paywalls/public/DynamicPaywallLoader';

@Service()
export class SlLessonService {
  private api = Container.take(SlLEContext, SlApiService);

  private data = Container.take(SlLEContext, SlDataService);

  private actions$ = Container.take(SlLEContext, SlActions).actions$;

  private navigation = Container.take(SlLEContext, SlNavigationService);

  private streakService = Container.take('global', StreakService);

  private heartService = Container.take('global', HeartService);

  private bitService = Container.take('global', BitService);

  private storageService = Container.take('global', StorageService);

  private globalDataService = Container.take('global', DataService);

  private subscriptionPlanService = Container.take('global', SubscriptionPlanService);

  private paywallLoader = DynamicPaywallLoader.ensureRegistered();

  private sessionRouteService = Container.take(
    'global',
    SessionRoutesService,
  );

  private handleError = Container.take(SlLEContext, SlErrorHandlerService)
    .handleError;

  private currentLessonId: number = null;

  constructor() {
    const unsubscriber: Subject<void> = Container.take(
      SlLEContext,
      'unsubscriber',
    ) || new Subject<void>();

    const lessonWhenChanged = this.data.lesson$.pipe(
      pairwise(),
      filter(
        ([prev, curr]) => !!curr && (prev?.materialInfo.header.dynamicId !== curr.materialInfo.header.dynamicId)
      ),
      map(([, curr]) => curr),
    );

    this.subscriptionPlanService.subscriptionPlanConfigs
      .pipe(
        filter(c => !!c),
        takeUntil(unsubscriber)
      ).subscribe(() => {
        if (this.data?.lesson$?.value?.materialInfo?.header?.dynamicId) {
          const sub = this.setLessonData(
            this.data.lesson$.value.materialInfo.header.dynamicId,
            this.data.lessonPageIndex$.value, false, true
          ).subscribe(() => {
            sub.unsubscribe();
          });
        }
      });

    merge(
      combineLatest([this.data.course$, lessonWhenChanged]).pipe(
        filter(([course]) => !!course),
        tap(([course, lesson]) => this.setLessonOrderInCourse(course, lesson)),
      ),
      combineLatest([this.data.learningPath$, lessonWhenChanged]).pipe(
        filter(([path]) => !!path),
        tap(([path, lesson]) => this.setCurrentCourseFromPath(path, lesson)),
      ),

    ).pipe(takeUntil(unsubscriber)).subscribe();
  }

  public removeLessonSessionDataRecording = (): Observable<null> => {
    const routesData = this.sessionRouteService.getRoutesData(
      SessionRouteEnums.lessonSessionRoutes,
    );

    if (routesData.routes.length === 0) {
      this.actions$.next({
        type: ActionType.DeleteSessionDataFromStorage,
        requestType: RequestType.Parallel,
      });
    } else {
      const nonRemovableRoutes = routesData.routes.filter((item) => !NonRemovableLessonSessionRoutes.some((member) => item.includes(member)));

      // We can check this cause this function calls before routeData$ setting
      if (
        nonRemovableRoutes.length > 1
        || this.data.routeData$.value?.currentPage
        === CurrentPageType.CoursePage
      ) {
        this.actions$.next({
          type: ActionType.DeleteSessionDataFromStorage,
          requestType: RequestType.Parallel,
        });
      }
    }
    this.sessionRouteService.removeRoutesData(
      SessionRouteEnums.lessonSessionRoutes,
    );
    this.sessionRouteService.sessionRoutesInfo.next({
      [SessionRouteEnums.lessonSessionRoutes]: false,
    });
    return of(null);
  };

  public startLessonSessionDataRecording = (): Observable<null> => {
    if (this.storageService.load(SlStorageKeys.Sl_LE_SESSION, 'session')) {
      this.sessionRouteService.startSessionRoutesRecording(
        SessionRouteEnums.lessonSessionRoutes,
        { lessonRelationId: this.data.lesson$.value?.materialInfo.header.dynamicId },
      );
      this.sessionRouteService.sessionRoutesInfo.next({
        [SessionRouteEnums.lessonSessionRoutes]: true,
      });
    }
    return of(null);
  };

  public openLesson = (materialRelationId: number): Observable<null> => {
    const { courseAlias, pathAlias } = this.data.routeData$.value;
    this.navigation.navigation$.next({
      materialRelationId,
      courseAlias,
      pathAlias,
    });
    return of(null);
  };

  public tryOpenFirstLesson = (): Observable<null> => {
    const { lesson$ } = this.data;
    if (lesson$.value) {
      this.openLesson(lesson$.value.materialInfo.header.dynamicId);
    }
    return of(null);
  };

  private tryLeaveLesson = (): void => {
    const { course$, lesson$, routeData$ } = this.data;
    const { materialId, courseAlias, pathAlias } = routeData$.value;
    const { parentRelationId } = lesson$.value.materialInfo.header;

    const parent: IMaterial = course$.value.children.find(
      (item) => item.materialInfo.header.materialRelationId === parentRelationId,
    );

    if (parent) {
      const currentLessonIndex = parent.children.findIndex(
        (item) => materialId === item.materialInfo.header.materialRelationId,
      );
      const isNextCodeProject = parent.children[currentLessonIndex + 1]?.materialInfo.header
        .typeId === MaterialType.CodeProject;
      if (isNextCodeProject) {
        this.actions$.next({
          type: ActionType.OpenCodeProject,
          requestType: RequestType.Parallel,
          data: {
            id: parent.children[currentLessonIndex + 1].materialInfo
              .header.materialRelationId,
            returnUrl: `/learn/${courseAlias
              ? `courses/${courseAlias}`
              : `learning-paths/${pathAlias}`
              } `,
          },
        });
      } else {
        this.closeLesson();
      }
    } else {
      this.closeLesson();
    }

    this.data.lessonStatus = null;
  };

  public closeLesson = (): Observable<null> => {
    this.data.lessonStatus = null;
    this.navigation.navigation$.next({ toLEPage: true });

    this.actions$.next({
      type: ActionType.DeleteSessionFromBackend,
      requestType: RequestType.Parallel,
      data: {
        lessonSessionQuitType: LessonSessionQuitType.LessonQuizModal,
      },
    });
    this.actions$.next({
      type: ActionType.DeleteSessionDataFromStorage,
      requestType: RequestType.Parallel,
    });

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialQuit,
          quitAction: MaterialQuitAction.XButton,
        },
      },
    });

    return of(null);
  };

  public deleteSessionFromBackend = (
    quitType: LessonSessionQuitType,
  ): Observable<null> => {
    const dynamicId = this.data.lesson$.value?.materialInfo?.header.dynamicId;

    return this.api.deleteSession(dynamicId, quitType).pipe(catchError(() => of(null)));
  };

  public deleteLessonSessionData = (): Observable<null> => {
    const sessionId: string = this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );
    if (!sessionId) return of(null);

    this.storageService.remove(SlStorageKeys.Sl_LE_SESSION, 'session');

    return of(null);
  };

  public completeLesson = (): Observable<null> => {
    history.push(`${window.location.pathname}/complete`);
    this.trySetLCSIndex(0);
    return of(null);
  };

  private isLastLesson = (): boolean => {
    const { lessonPageIndex$, lesson$ } = this.data;
    const lessonPageIndex: number = lessonPageIndex$.value;
    const lessonChildren: IMaterial[] = lesson$.value.children;

    return lessonPageIndex === lessonChildren.length;
  };

  public byPassLesson = (): Observable<null> => {
    if (!this.isLastLesson()) {
      this.actions$.next({
        type: ActionType.ToNextPage,
        requestType: RequestType.Parallel,
      });
    } else {
      this.actions$.next({
        type: ActionType.CompleteLesson,
        requestType: RequestType.Parallel,
      });
    }
    return of(null);
  };

  private getLessonSubTreeErrorHandler = (err: IError[]) => {
    const { flashMessage$ } = Container.take(SlLEContext, SlDataService);

    if (!Array.isArray(err)) {
      this.navigation.navigation$.next({
        toNotFoundLesson: true,
      });
      return;
    }
    this.handleError(err, {
      [ErrorCodes.BadRequest]: () => {
        this.navigation.navigation$.next({
          toNotFoundLesson: true,
        });
      },
      [ErrorCodes.NotFound]: () => {
        this.navigation.navigation$.next({
          toNotFoundLesson: true,
        });
      },
      [ErrorCodes.Forbidden]: () => {
        this.navigation.navigation$.next({
          toLEPage: true,
        });
        flashMessage$.next({
          type: IFlashMessageTypes.INFO,
          message: getFlashMessages().not_found,
        });
      },
      [ErrorCodes.OwnershipFailure]: () => {
        this.navigation.navigation$.next({
          toLEPage: true,
        });
      },
      [ErrorCodes.NotEnoughHearts]: () => {
        this.navigation.navigation$.next({
          toLEPage: true,
          query: '?openheartmodal=true',
        });
      },
    });
  };

  public setLessonData = (
    materialRelationIdParam: number,
    lessonPageIndexParam?: number,
    isTranslating = false,
    forceUpdate = false
  ): Observable<ILessonSubtree> => {
    const materialRelationId = materialRelationIdParam
      || this.data.course$.value?.children?.[0]?.children?.[0]?.materialInfo?.header?.materialRelationId;

    if (!materialRelationId) {
      return of(null);
    }

    const { lesson$, loading$ } = this.data;

    if (
      !lesson$.value
      || (lesson$.value.materialInfo.header.materialRelationId
        !== materialRelationId && !this.data.isSkipAhead.value)
      || isTranslating
      || forceUpdate
    ) {
      const session: string = this.storageService.load(
        SlStorageKeys.Sl_LE_SESSION,
        'session',
      );
      const headers = getLEHeaders(!!this.data.isSkipAhead.value);
      if (!isTranslating) {
        loading$.next({ fullScreen: true });
      }

      const apiCall$ = this.data.isSkipAhead.value ? this.api.getSkipAheadSubTree(materialRelationId, session, headers)
        : this.api.getLessonSubTree(materialRelationId, session, headers);

      return apiCall$.pipe(
        tap((lessonSubtree) => {
          if (!lessonSubtree) {
            this.navigation.navigation$.next({
              toNotFoundLesson: true,
            });
            loading$.next(null);
            return of(null);
          }

          if (lessonSubtree.session) {
            this.storageService.save(
              SlStorageKeys.Sl_LE_SESSION,
              lessonSubtree.session,
              'session',
            );
          }
          lesson$.next(lessonSubtree.tree);
          this.prefetchAssets(lessonSubtree.tree);

          if (
            this.currentLessonId !== materialRelationId
            || !this.data.lessonStatus
          ) {
            this.data.lessonStatus = lessonSubtree.tree.materialInfo.status;
            this.currentLessonId = materialRelationId;
          }

          if (
            this.data.routeData$.value.locationUrl
            === LocationType.Onboarding
          ) {
            this.actions$.next({
              type: ActionType.OnOnboardingLessonReady,
              requestType: RequestType.Parallel,
            });
          } else {
            this.setLessonPage(
              materialRelationId,
              lessonPageIndexParam,
            );
          }

          loading$.next(null);
          return of(null);
        }),
        catchError((err) => {
          this.getLessonSubTreeErrorHandler(err);
          loading$.next(null);
          return of(null);
        }),
      );
    }
    this.setLessonPage(materialRelationId, lessonPageIndexParam);

    return of(null);
  };

  public navigateToLessonPage = (
    materialRelationId: number,
    pageIndex?: number,
    replace = false,
  ): Observable<null> => {
    const lessonPageIndex = this.getValidLessonPageIndex(pageIndex);
    this.data.routeData$.next({
      ...this.data.routeData$.value,
      lessonPageIndex,
      currentPage: CurrentPageType.LessonPage,
    });
    this.data.lessonPageIndex$.next(lessonPageIndex);

    this.navigation.navigation$.next({
      materialRelationId,
      query: `?p=${lessonPageIndex}`,
      options: {
        replace,
      },
      isSkipAhead: this.data.isSkipAhead.value,
    });
    return of(null);
  };

  public initializeLessonPage = (): Observable<null> => {
    const { children } = this.data.lesson$.value;
    const lessonPageIndex = this.data.lessonPageIndex$.value;

    if (
      this.data.lesson$.value.materialInfo.header.typeId
      === MaterialType.Booster
    ) {
      this.setLPComments({
        ...this.data.lpComments$.value,
        hide: true,
      });
    } else {
      const commentContainerId = children[lessonPageIndex - 1].materialInfo.commentContainerId;

      if (commentContainerId) {
        this.api
          .getLPCommentsCount(commentContainerId)
          .pipe(
            tap((count) => {
              this.setLPComments({
                ...this.data.lpComments$.value,
                count,
              });
            }),
            catchError(() => {
              this.data.lpComments$.next(null);
              return of(null);
            }),
          )
          .subscribe();
      }
    }

    const question = children[lessonPageIndex - 1].children?.find(
      (mat) => mat.materialInfo.header?.typeId === MaterialType.Question,
    );
    this.data.question$.next(question?.materialInfo || null);

    if (
      question
      && question.materialInfo.answer.answerTypeId !== AnswerType.TypeIn
      && question.materialInfo.answer.answerTypeId !== AnswerType.MultiTypeIn
    ) {
      const answerOptions: IAnswerOption[] = question.materialInfo.answer.options?.map((o) => ({
        ...o,
        status: AnswerOptionStatus.Initial,
      }));
      this.data.answerOptions$.next(answerOptions);
    }

    this.initializeFooterButtons();

    return of(null);
  };

  private setLessonPage = (
    materialRelationId: number,
    lessonPageIndex: number,
  ) => {
    if (this.data.routeData$.value.currentPage === CurrentPageType.LessonCompletedPage) {
      return;
    }

    if (this.isValidLessonPageIndex(lessonPageIndex)) {
      this.data.lessonPageIndex$.next(lessonPageIndex);
    } else {
      this.navigateToLessonPage(
        materialRelationId,
        lessonPageIndex,
        true,
      );
    }
  };

  public submitSolutions = (
    solutions: IMaterialSolutionSubmission[],
    questionStatus: IMaterialStatus = null,
  ): Observable<null> => {
    const { isCorrect, answer } = solutions[0];

    if (answer?.selectedOptions) {
      if (
        this.getCurrentLessonPage()?.materialInfo.status.completion !== CompletionStatus.ByPassed
        && answer.answerTypeId === AnswerType.MultiChoice
        && answer.selectedOptions.length === 1
        && answer.selectedOptions[0].isCorrect
      ) {
        this.data.quizFeedback$.next(QuizFeedbackType.FewOptions);
      } else {
        if (this.data.lpComments$.value) {
          this.setLPComments({
            ...this.data.lpComments$.value,
            disabled: false,
          });
        }

        this.updateQuizFeedback(isCorrect, questionStatus);
      }

      this.updateAnswerOptions(answer.selectedOptions);
    }

    this.data.quizFeedbackFromBackend$.next(answer?.feedback);
    this.setFooterPrmBtn(solutions[0]);
    this.setFooterContBtn(solutions[0]);
    return of(null);
  };

  public toPreviousPage = (): Observable<null> => {
    const {
      lessonFooterPrmBtn$,
      lessonFooterSecBtn$,
      lessonPageIndex$,
      lesson$,
      quizFeedback$,
      disabledActions$,
    } = this.data;
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialCTAClick,
          materialCTAClickType: MaterialCTAClickType.BackButton,
        },
      },
    });
    if (lessonFooterPrmBtn$.value && !lessonFooterPrmBtn$.value.disabled) {
      lessonFooterPrmBtn$.next({
        ...lessonFooterPrmBtn$.value,
        disabled: true,
      });
    }
    lessonFooterSecBtn$.next({
      ...lessonFooterSecBtn$.value,
      disabled: true,
    });
    quizFeedback$.next(null);
    disabledActions$.next(null);

    const materialId = this.data.isSkipAhead.value ? this.data.routeData$.value.materialId : lesson$.value.materialInfo.header.materialRelationId;

    return this.navigateToLessonPage(
      materialId,
      lessonPageIndex$.value - 1,
    );
  };

  public toNextPage = (): Observable<null> => {
    const {
      lessonFooterPrmBtn$,
      lessonFooterSecBtn$,
      lessonPageIndex$,
      lesson$,
      quizFeedback$,
      disabledActions$,
    } = this.data;
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialCTAClick,
          materialCTAClickType: MaterialCTAClickType.Continue,
        },
      },
    });
    lessonFooterPrmBtn$.next({
      ...lessonFooterPrmBtn$.value,
      disabled: true,
    });
    if (lessonFooterSecBtn$.value && !lessonFooterSecBtn$.value.disabled) {
      lessonFooterSecBtn$.next({
        ...lessonFooterSecBtn$.value,
        disabled: true,
      });
    }
    quizFeedback$.next(null);
    disabledActions$.next(null);

    const materialId = this.data.isSkipAhead.value ? this.data.routeData$.value.materialId : lesson$.value.materialInfo.header.materialRelationId;

    return this.navigateToLessonPage(
      materialId,
      lessonPageIndex$.value + 1,
    );
  };

  public checkAnswer = (): Observable<null> => {
    const { question$, answerOptions$, disabledActions$ } = this.data;

    if (disabledActions$.value?.question) {
      return of(null);
    }

    const {
      header: { typeId, materialRelationId },
      answer: { answerTypeId },
    } = question$.value;
    const selectedOptions = answerOptions$.value.filter(
      (o) => o.status !== AnswerOptionStatus.Initial,
    );
    this.data.unlockQuizAnswerBtn$.next({
      ...this.data.unlockQuizAnswerBtn$.value,
      disabled: true,
    });

    this.actions$.next({
      type: ActionType.SolveMaterial,
      requestType: RequestType.Cancelable,
      data: {
        solutions: [
          {
            typeId,
            materialRelationId,
            answer: {
              answerTypeId,
              selectedOptions,
            },
          },
        ],
      },
    });

    return of(null);
  };

  public tryAgain = (): Observable<null> => {
    const {
      answerOptions$,
      question$,
      lessonFooterPrmBtn$,
      quizFeedback$,
      disabledActions$,
    } = this.data;
    if (!answerOptions$.value) {
      return of(null);
    }
    const options: IAnswerOption[] = answerOptions$.value.map((o) => {
      const removeText = question$.value.answer.answerTypeId === AnswerType.TypeIn
        || question$.value.answer.answerTypeId === AnswerType.MultiTypeIn;
      return {
        ...o,
        isCorrect: null,
        status: AnswerOptionStatus.Initial,
        text: removeText ? '' : o.text,
      };
    });

    if (this.data.lpComments$.value) {
      this.setLPComments({
        ...this.data.lpComments$.value,
        disabled: true,
      });
    }

    if (
      question$.value
      && question$.value.answer.answerTypeId !== AnswerType.TypeIn
      && question$.value.answer.answerTypeId !== AnswerType.MultiTypeIn
    ) {
      answerOptions$.next(shuffleArray(options));
    } else {
      answerOptions$.next(options);
    }

    lessonFooterPrmBtn$.next(getFooterButtons()[ActionType.CheckAnswer]);

    this.data.unlockQuizAnswerBtn$.next({
      ...this.data.unlockQuizAnswerBtn$.value,
      disabled: false,
    });
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialCTAClick,
          materialCTAClickType: MaterialCTAClickType.TryAgain,
        },
      },
    });
    quizFeedback$.next(null);
    disabledActions$.next(null);
    this.setFooterContBtn();

    return of(null);
  };

  private isValidLessonPageIndex = (pageIndex?: number): boolean => {
    const { children } = this.data.lesson$.value;
    return (
      pageIndex
      && children[pageIndex - 1]?.materialInfo.status.visibility
      === VisibilityStatus.Unlocked
    );
  };

  private getCurrentLessonPageIndex = (): number => {
    const {
      materialInfo: {
        status: { isCompleted },
      },
      children,
    } = this.data.lesson$.value;

    if (isCompleted) {
      return 1;
    }

    const firstIncompleteChildIndex = children.findIndex(
      ({ materialInfo }) => !materialInfo.status?.isCompleted,
    );

    return firstIncompleteChildIndex === -1
      ? 1
      : firstIncompleteChildIndex + 1;
  };

  private getValidLessonPageIndex = (pageIndex?: number): number => (this.isValidLessonPageIndex(pageIndex)
    ? pageIndex
    : this.getCurrentLessonPageIndex());

  private initializeFooterButtons = (): void => {
    this.setFooterPrmBtn();
    this.setFooterSecBtn();
    this.setFooterContBtn();
    this.setUnlockQuizAnswerBtn();
  };

  private setFooterPrmBtn = (
    solution?: IMaterialSolutionSubmission,
  ): void => {
    const {
      question$, lessonPageIndex$, lesson$, lessonFooterPrmBtn$,
    } = this.data;
    let lessonFooterPrmBtn: IFooterButton = null;

    if (solution && !solution.isCorrect) {
      const isRequired = solution.completion === CompletionStatus.NotCompleted || solution.completion === CompletionStatus.InProgress || solution.completion === CompletionStatus.Skipped;

      const defineButtonType = () => {
        if (isRequired) {
          return ActionType.TryAgain;
        }
        if (this.isLastLesson()) {
          return ActionType.CompleteLesson;
        }
        return ActionType.ToNextPage;
      };

      lessonFooterPrmBtn = {
        ...getFooterButtons()[
        defineButtonType()
        ],
      };
    } else if (
      question$.value
      && (!solution || solution.typeId !== MaterialType.Question)
    ) {
      lessonFooterPrmBtn = {
        ...getFooterButtons()[ActionType.CheckAnswer],
      };
    } else {
      if (!lesson$.value) {
        return;
      }
      const lessonPageIndex: number = lessonPageIndex$.value;
      const lessonChildren: IMaterial[] = lesson$.value.children;

      if (lessonPageIndex < lessonChildren.length) {
        lessonFooterPrmBtn = {
          ...getFooterButtons()[ActionType.ToNextPage],
        };
      } else if (lessonPageIndex === lessonChildren.length) {
        lessonFooterPrmBtn = {
          ...getFooterButtons()[ActionType.CompleteLesson],
        };
      }
      lessonFooterPrmBtn.disabled = lessonChildren[
        lessonPageIndex - 1
      ]?.children?.some(
        ({
          materialInfo: {
            requirementTypeId,
            status: { isCompleted },
            header: { materialRelationId },
          },
        }) => (requirementTypeId === MaterialRequirementType.Required
          || requirementTypeId
          === MaterialRequirementType.RequiredForParentCompletion)
        && !isCompleted
          && materialRelationId !== solution?.materialRelationId,
      );
    }
    lessonFooterPrmBtn$.next(lessonFooterPrmBtn);
  };

  private setFooterSecBtn = (): void => {
    const { lessonPageIndex$, lessonFooterSecBtn$ } = this.data;

    const lessonFooterSecBtn: IFooterButton = lessonPageIndex$.value > 1
      ? getFooterButtons()[ActionType.ToPreviousPage]
      : null;

    lessonFooterSecBtn$.next(lessonFooterSecBtn);
  };

  private setFooterContBtn = (solution?: IMaterialSolutionSubmission) => {
    const {
      lessonPageIndex$,
      lessonFooterPrmBtn$,
      lesson$,
      lessonFooterContBtn$,
    } = this.data;

    // if current lesson is completed, show byPassed button with primary, and primary button with secondary type
    if (
      (lesson$.value.children[lessonPageIndex$.value - 1]?.materialInfo
        .status.completion === CompletionStatus.ByPassed
        || lesson$.value.children[lessonPageIndex$.value - 1]?.materialInfo
          .status.completion === CompletionStatus.Completed)
      && solution
      && !solution?.isCorrect
    ) {
      lessonFooterContBtn$.next(
        getFooterButtons()[ActionType.ByPassLesson],
      );
      return;
    }
    if (
      lesson$.value.children[lessonPageIndex$.value - 1].materialInfo
        .status?.isCompleted
    ) {
      if (
        lessonPageIndex$.value === lesson$.value.children.length
        && lessonFooterPrmBtn$.value?.action !== ActionType.CompleteLesson
      ) {
        lessonFooterContBtn$.next(
          getFooterButtons()[ActionType.CompleteLesson],
        );
      } else if (
        lessonPageIndex$.value !== lesson$.value.children.length
        && lessonFooterPrmBtn$.value?.action !== ActionType.ToNextPage
      ) {
        lessonFooterContBtn$.next(
          getFooterButtons()[ActionType.ToNextPage],
        );
      } else {
        lessonFooterContBtn$.next(null);
      }
    } else {
      lessonFooterContBtn$.next(null);
    }
  };

  private setUnlockQuizAnswerBtn = (): void => {
    const { question$, unlockQuizAnswerBtn$ } = this.data;

    let unlockQuizAnswerBtn: IFooterButton = null;
    if (question$.value) {
      unlockQuizAnswerBtn = getFooterButtons()[ActionType.OpenUnlockQuizAnswerModal];
    }

    unlockQuizAnswerBtn$.next(unlockQuizAnswerBtn);
  };

  private updateAnswerOptions = (options: IAnswerOption[]): void => {
    const { answerOptions$ } = this.data;
    const answerOptions = answerOptions$.value?.map((option) => {
      if (option.status === AnswerOptionStatus.Initial) {
        return option;
      }
      const updatedOption = options.find(
        (o) => o.id === option.id || o.orderNumber === option.orderNumber,
      );
      return {
        ...option,
        isCorrect: updatedOption?.isCorrect,
        status: updatedOption?.isCorrect
          ? AnswerOptionStatus.Correct
          : AnswerOptionStatus.Incorrect,
      };
    });

    answerOptions$.next(answerOptions);
  };

  private getCurrentLessonPage = (): IMaterial => {
    const { lesson$, lessonPageIndex$ } = this.data;
    return lesson$.value.children[lessonPageIndex$.value - 1];
  };

  private updateQuizFeedback = (
    isCorrect: boolean,
    questionStatus: IMaterialStatus,
  ): void => {
    const lessonPageStatus = this.getCurrentLessonPage()?.materialInfo.status.completion;
    const incorrectType = lessonPageStatus === CompletionStatus.ByPassed
      ? QuizFeedbackType.ByPassedIncorrect
      : QuizFeedbackType.Incorrect;
    let quizFeedback = isCorrect ? QuizFeedbackType.Correct : incorrectType;

    if (
      questionStatus
      && questionStatus.completion !== CompletionStatus.Completed
    ) {
      const correctInARow = isCorrect
        ? this.data.correctAnswersCountInARow$.value + 1
        : 0;
      if (correctInARow === 3) {
        quizFeedback = QuizFeedbackType.Correct3InARow;
        this.data.progressBarCelebration$.next(true);
      } else if (correctInARow === 5) {
        quizFeedback = QuizFeedbackType.Correct5InARow;
        this.data.progressBarCelebration$.next(true);
      }
      this.data.correctAnswersCountInARow$.next(correctInARow);
    }

    this.data.quizFeedback$.next(quizFeedback);
  };

  public defineLessonQuitPromptInfo = (): Observable<null> => {
    // if lesson is completed or hearts have been finished, do not show quit prompt

    const { lesson$, lessonQuitPromptInfo$ } = this.data;
    const currentLessonIsCompleted = lesson$.value.materialInfo.status?.isCompleted;

    const hasSessionId = !!this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );

    const hasHearts =
      this.heartService.hearts$.value?.heartsCount !== 0
      || this.subscriptionPlanService.getConfig(PlanConfigKey.hearts) === 'unlimited';

    if (hasSessionId) {
      lessonQuitPromptInfo$.next({
        ...lessonQuitPromptInfo$.value,
        shouldShow: !currentLessonIsCompleted && (hasHearts),
        popupData: getLoseProgressPromptPopup(),
        type: LessonQuitPopupType.LOSE_PROGRESS,
      });
    } else {
      lessonQuitPromptInfo$.next({
        ...lessonQuitPromptInfo$.value,
        shouldShow: !currentLessonIsCompleted && (hasHearts),
        popupData: getLessonQuitPromptPopup(),
        type: LessonQuitPopupType.LEAVE_LESSON,
      });
    }

    return of(null);
  };

  public clearLessonData = (): Observable<null> => {
    const {
      lessonFooterPrmBtn$,
      lessonFooterSecBtn$,
      answerOptions$,
      question$,
      quizFeedback$,
      lesson$,
      lessonBackup$,
      lessonPageIndex$,
      openUnlockQuizAnswerModal$,
      correctAnswersCountInARow$,
      progressBarCelebration$,
    } = this.data;
    this.actions$.next({
      type: ActionType.StartLessonSessionDataRecording,
      requestType: RequestType.Parallel,
    });
    lessonFooterPrmBtn$.next(null);
    lessonFooterSecBtn$.next(null);
    answerOptions$.next(null);
    question$.next(null);
    quizFeedback$.next(null);
    lessonBackup$.next(lesson$.value);
    lesson$.next(null);
    lessonPageIndex$.next(null);
    openUnlockQuizAnswerModal$.next(false);
    correctAnswersCountInARow$.next(0);
    progressBarCelebration$.next(false);

    return of(null);
  };

  public openUnlockQuizAnswerModal = (): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.MaterialSolutionClick,
        },
      },
    });
    this.data.openUnlockQuizAnswerModal$.next(true);
    return of(null);
  };

  public unlockQuizAnswer = (): Observable<IMaterialSolutionDTO> => {
    const { flashMessage$ } = Container.take(SlLEContext, SlDataService);
    const { question$, loading$ } = this.data;
    const { shopItemsList$ } = this.globalDataService;
    if (!question$.value) return of(null);

    loading$.next({ unlock: true });
    const bits = this.bitService.bits$.value?.bits;
    const sessionId: string = this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );

    return this.api
      .getMaterialSolution(
        question$.value.header.materialRelationId,
        sessionId,
      )
      .pipe(
        tap(({ answerSolutions }) => {
          this.actions$.next({
            type: ActionType.Track,
            requestType: RequestType.Parallel,
            data: {
              trackData: {
                actionName:
                  TrackActionName.MaterialSolutionImpression,
              },
            },
          });
          this.handleAnswerSolutions(answerSolutions);
          const { price } = shopItemsList$.value.find(
            (si) => si.id === ShopItemType.QuizAnswer,
          );
          this.actions$.next({
            type: ActionType.DecreaseBitsCount,
            requestType: RequestType.Parallel,
            data: {
              numberValue: bits,
              price,
            },
          });
        }),
        catchError(err => {
          flashMessage$.next({
            type: IFlashMessageTypes.INFO,
            message: i18n.t('learn.unlock.error.message'),
          });
          loading$.next({ unlock: false });
          return EMPTY;
        })
      );
  };

  public setLPComments = (comments: ILpComments): Observable<null> => {
    const { lpComments$, lesson$, routeData$ } = this.data;
    if (!lesson$.value) {
      return of(null);
    }

    if (
      !lesson$.value.children[
        routeData$.value.lessonPageIndex - 1
      ].children.find(
        (item) => item.materialInfo.header.typeId === MaterialType.Question,
      )
    ) {
      lpComments$.next({
        ...comments,
        disabled: false,
        count: comments?.count ?? lpComments$.value?.count,
      });
      return of(null);
    }
    const isCompleted = lesson$.value.children[routeData$.value.lessonPageIndex - 1]
      .materialInfo.status?.isCompleted;
    if (isCompleted) {
      lpComments$.next({
        ...comments,
        disabled: false,
        count: comments?.count ?? lpComments$.value?.count,
      });
      return of(null);
    }
    if (typeof comments?.disabled !== 'undefined') {
      lpComments$.next({
        ...lpComments$.value,
        ...comments,
        disabled: comments.disabled,
        count: comments.count ?? lpComments$.value.count,
      });
      return of(null);
    }
    lpComments$.next({
      ...comments,
      disabled: true,
      count: comments?.count ?? lpComments$.value?.count,
    });

    return of(null);
  };

  private handleAnswerSolutions = (
    answerSolutions: IAnswerOption[],
  ): void => {
    const { answerOptions$, question$, loading$ } = this.data;
    const {
      answer: { answerTypeId },
    } = question$.value;
    loading$.next({
      unlock: false,
    });

    let updatedAnswerOptions = answerOptions$.value.map((ao, i) => {
      const solution = answerTypeId === AnswerType.TypeIn
        || answerTypeId === AnswerType.MultiTypeIn
        ? answerSolutions[i]
        : answerSolutions.find((as) => as.id === ao.id);

      return {
        ...ao,
        text: solution?.text || ao.text,
        status: solution
          ? AnswerOptionStatus.Selected
          : AnswerOptionStatus.Initial,
        orderNumber: solution ? solution.orderNumber : null,
      };
    });

    if (question$.value.answer.answerTypeId === AnswerType.Rearrange) {
      updatedAnswerOptions = updatedAnswerOptions.sort(
        (o1, o2) => o1.orderNumber - o2.orderNumber,
      );
    }

    answerOptions$.next(updatedAnswerOptions);

    this.checkAnswer();
  };

  public getLessonCompleteScreens = (): Observable<IScreenConfig[]> => this.api.getScreensConfig(ScreenSource.LessonComplete).pipe(
    tap((screens: IScreenConfig[]) => {
      this.data.lessonCompleteScreens$.next(
        screens
          .sort((s1, s2) => s1.order - s2.order)
          .filter((s) => SUPPORTED_SCREENS.includes(s.name)),
      );
    }),
    catchError(() => of(null)),
  );

  public nextLessonCompleteScreenIteration = (): Observable<null> => {
    const params = new URLSearchParams(window.location.search);
    this.trySetLCSIndex(Number(params.get('i')) + 1);
    return of(null);
  };

  private trySetLCSIndex = (index: number): void => {
    this.data.lessonCompleteScreens$.pipe(
      first((s) => !!s),
      tap((screens) => {
        if (!screens?.length || index === screens?.length) {
          this.tryLeaveLesson();
          return;
        }

        const screen = screens[index].name;

        if (!this.shouldOpenScreen(screen)) {
          this.trySetLCSIndex(index + 1);
          return;
        }

        this.navigation.navigation$.next({
          query: `?i=${index}`,
        });
      }),
    ).subscribe();
  };

  private shouldOpenScreen = (screen: ScreenName): boolean => {
    switch (screen) {
      case ScreenName.Paywall:
        return this.shouldOpenPaywall();
      case ScreenName.StreakCelebration:
        return this.shouldOpenStreakCelebration();
      case ScreenName.StreakGoal:
        return this.shouldOpenStreakGoal();
      case ScreenName.LeaderboardCelebration:
        return this.shouldOpenLeaderboardScreen();
      default:
        return true;
    }
  };

  private shouldOpenPaywall = (): boolean => {
    const { orderNumber } = this.data.lesson$.value.materialInfo;
    const showLessonCompletePaywall = this.subscriptionPlanService.getConfig(PlanConfigKey.showLessonCompletePaywall) as boolean;
    return showLessonCompletePaywall && orderNumber !== 1;
  };

  private shouldOpenStreakCelebration = (): boolean => (
    !this.data?.lessonStatus?.isCompleted
    && this.streakService.wasLastAchieveSuccessful()
  );

  private shouldOpenStreakGoal = (): boolean => (
    !this.data.lessonStatus?.isCompleted
    && this.data.lesson$.value.materialInfo.header.typeId
    !== MaterialType.Booster
    && !!this.streakService.configuration$.value?.goalConfig
    && this.streakService.wasLastAchieveSuccessful()
    && this.streakService.isFirstAchieve()
  );

  private shouldOpenLeaderboardScreen = (): boolean => (
    this.data.isLeaderboardAvailable
    && this.data.lessonOrderInCourse$.value !== 1
  );

  private setLessonOrderInCourse = (
    course: IMaterial,
    lesson: IMaterial,
  ): void => {
    let moduleIndex: number;
    let lessonIndex: number;
    for (
      moduleIndex = 0, lessonIndex = 0;
      moduleIndex < course.children.length;
      ++moduleIndex
    ) {
      const module = course.children[moduleIndex];
      for (
        let lessonIndexInModule = 0;
        lessonIndexInModule < module.children.length;
        ++lessonIndexInModule, ++lessonIndex
      ) {
        if (
          module.children[lessonIndexInModule].materialInfo.header
            .materialRelationId
          === lesson.materialInfo.header.materialRelationId
        ) {
          this.data.lessonOrderInCourse$.next(lessonIndex + 1);
          return;
        }
      }
    }
  };

  private setCurrentCourseFromPath = (
    path: ILearningPathSubtree,
    lesson: IMaterial,
  ): void => {
    for (let unitIndex = 0; unitIndex < path.units.length; ++unitIndex) {
      const course = path.units[unitIndex].courseSubtree;
      for (
        let moduleIndex = 0;
        moduleIndex < course.children.length;
        ++moduleIndex
      ) {
        if (
          course.children[moduleIndex].children.some(
            (l) => l.materialInfo.header.id
              === lesson.materialInfo.header.id,
          )
        ) {
          this.data.course$.next(course);
          return;
        }
      }
    }
  };

  public trackStreakCelebrationImpression = (): void => this.streakService.trackStreakCelebrationImpression();

  public prefetchAssets = (tree: IMaterial): void => {
    tree.children.map((branch) => {
      if (branch.children) {
        this.prefetchAssets(branch);
      } else if (branch.materialInfo?.body) {
        branch.materialInfo?.body.forEach((material) => {
          if (material.componentTypeId === UIComponentType.RiveAnimation && !!material.config?.rive) {
            fetch(material.config.rive.webAssetUrl);
          }

          if (
            material.componentTypeId === UIComponentType.Gif
            || material.componentTypeId === UIComponentType.Image
          ) {
            const img = new Image();
            img.src = material.content.data;
          }
        });
      }

      return null;
    });
  };

  public openLessonCompletePaywall = () => {
    this.paywallLoader.openPaywall({
      touchpoint: Touchpoint.lessonComplete,
      props: {},
    }, {
      onClose: () => {
        this.nextLessonCompleteScreenIteration();
      }
    });
  };
}
